<template>
    <div class="splash-card" v-if="loading">
        <be-alert border icon-colored icon="close" color="danger" v-if="!assessment.isAssessmentCompleted">
            <strong>Digital.Assessment ausstehend</strong>
            <p>Führen Sie zuerst das Assessment durch. Danach können Sie unter diesem Punkt den Digital.Report dazu ausfüllen.</p>
            <router-link :to="{ name: 'assessment_survey' }" class="btn btn-primary">Zum Assessment</router-link>
        </be-alert>

        <be-alert border icon-colored icon="check" color="success" v-else-if="assessment.isAssessmentCompleted">
            <strong>Digital.Report bereit</strong>
            <p>Füllen Sie den Report aus um dieses Assessment abzuschliessen.</p>
            <button type="button" class="btn btn-primary" @click.prevent="initReport">Report erstellen</button>
        </be-alert>

        <div class="splash-card centered" v-else><be-spinner/></div>
    </div>

    <be-loading :active="submitting" v-else>
        <div class="invoice report">
            <report-header :assessment="assessment"/>
            <report-summary :assessment="assessment" :editable="editable" :comment.sync="formData.comment">
                <report-chart :organisation="assessment.organisationName" :items="items"/>
            </report-summary>

            <div class="break-before-always py-5" v-for="(item, i) in items" :key="item.id">
                <hr class="mt-5 d-print-none">
                <h2 class="my-5">Handlungsempfehlungen #{{i+1}}</h2>
                <report-score
                    :score="100 / item.totalScorePossible * item.score"
                    :title="item.frameworkCategory.title"
                    :description="item.frameworkCategory.claim"
                />
                <report-notes :editable="editable" :comment.sync="formData.reportItems[item.id].comment"/>
                <button
                    class="btn btn-primary btn-space float-right d-print-none"
                    v-if="editable"
                    @click.prevent="openSolutions(item.id)"
                >
                    <i class="icon mdi mdi-plus"></i>
                    Solution hinzufügen
                </button>
                <h3 v-if="editable || formData.reportItems[item.id].solutions.length">Lösungen</h3>
                <report-solutions
                    class="mt-4"
                    :editable="editable"
                    :ref="`solutions.${item.id}`"
                    :report-item="item"
                    v-model="formData.reportItems[item.id].solutions"
                />
            </div>

            <report-footer :assessment="assessment"/>

            <div class="row invoice-footer d-print-none mt-5">
                <div class="col-lg-12">
                    <template v-if="editable">
                        <button type="submit" class="btn btn-lg btn-space btn-primary" @click.prevent="save()">Ergebnis-Vorschau</button>
                    </template>
                    <template v-else-if="!assessment.isReportCompleted">
                        <button type="submit" class="btn btn-lg btn-space btn-secondary" @click.prevent="editReport">Report bearbeiten</button>
                        <button type="submit" class="btn btn-lg btn-space btn-primary" @click.prevent="save()">Assessment abschliessen</button>
                    </template>
                    <template v-else>
                        <submit-button large type="button" icon="print" @click="print()">Report drucken</submit-button>
                    </template>
                </div>
            </div>
        </div>
    </be-loading>
</template>

<script>
    import errorHandler from '../../../mixins/errorHandler';
    import Assessment from '../../../models/Assessment';

    import ReportHeader from './report/Header';
    import ReportSummary from './report/Summary';
    import ReportChart from './report/Chart';
    import ReportNotes from './report/Notes';
    import ReportScore from './report/Score';
    import ReportSolutions from './report/Solutions';
    import ReportFooter from './report/Footer';

    export default {
        mixins: [errorHandler],
        components: {
            ReportHeader,
            ReportSummary,
            ReportChart,
            ReportNotes,
            ReportScore,
            ReportSolutions,
            ReportFooter,
        },

        props: {
            assessment: {
                type: Assessment,
                required: true,
            },
        },

        data: () => ({
            initialized: false,
            loading: true,
            submitting: false,

            errorMessage: '',
            started: false,
            saved: false,

            items: [],
            formData: null,
        }),

        computed: {
            editable: vm => !vm.saved && !vm.assessment.isReportCompleted,
        },

        methods: {
            openSolutions(id) {
                this.$refs[`solutions.${id}`][0].open();
            },

            async initReport() {
                this.initialized = true;
                this.loading = true;

                this.formData = { comment: this.assessment.reportComment, reportItems: {} };
                this.started = !this.assessment.isReportCompleted;

                this.items = (await this.assessment.getReportItems()).sort(
                    (a, b) => (a.frameworkCategory.sorting > b.frameworkCategory.sorting ? 1 : -1),
                );

                this.items.forEach((item) => {
                    this.$set(this.formData.reportItems, item.id, {
                        id: item.resourceId,
                        comment: item.comment,
                        solutions: item.solutions,
                    });
                });

                this.loading = false;

                if (this.assessment.isReportCompleted) {
                    document.title = `${this.assessment.printableCreatedAt}-Digital.Report-${this.assessment.readableOrganisationWebsite}`;
                }
            },

            editReport() {
                this.saved = false;
                this.$scrollTo('.invoice');
            },

            async save(message = null) {
                let notification;
                const assessment = new Assessment({
                    id: this.assessment.id,
                });

                this.submitting = true;

                if (this.saved) {
                    assessment.setReportCompleted();

                    notification = {
                        title: 'Digital.Report abgeschlossen',
                        description: 'Ihre Änderungen wurden erfolgreich gespeichert. Drucken Sie das Ergebnis aus oder senden Sie dem Kunden den Link zu seinem Report.',
                        color: 'success',
                    };
                } else {
                    assessment.updateReport(this.formData.comment, Object.values(this.formData.reportItems));

                    notification = {
                        title: 'Digital.Report gespeichert',
                        description: message || 'Ihre Änderungen wurden erfolgreich gespeichert. Überprüfen Sie alle Eingaben und schliessen Sie den Report danach ab.',
                        color: 'success',
                    };
                }

                try {
                    this.$emit('update:assessment', await assessment.save());
                    this.$store.dispatch('notifications/add', notification);
                    this.$scrollTo('.invoice');

                    this.saved = true;
                    this.submitting = false;
                } catch (response) {
                    this.catchResponseWithDialog(
                        'Fehler beim Übermitteln des Digital.Report',
                        this.assessment,
                        response,
                    );
                }
            },

            print() {
                window.print();
            },
        },

        watch: {
            assessment() {
                if (this.initialized) {
                    this.initReport();
                }
            },
        },

        mounted() {
            if (this.assessment.isReportStarted || this.assessment.isReportCompleted) {
                this.initReport();
            }
        },

        async beforeRouteLeave(to, from, next) {
            if (from.name === 'report_new' || !this.started || !this.editable) {
                next();
                return;
            }

            await this.save('Ihre Änderungen wurden erfolgreich zwischengespeichert.');
            next();
        },

        destroyed() {
            document.title = 'Digital.Fitness';
        },
    };
</script>

<style rel="stylesheet/scss" lang="scss">
    .report {
        color: #000;
        font-size: 1.2rem;
        -webkit-print-color-adjust: exact;

        h2 {
            margin-top: 2rem !important;
        }
    }
</style>
